//@flow

import { declareAction, declareAtom } from '@reatom/core'
import axios from 'axios'

import * as common from '@bfg/common'

/**
 * Вариант пейволла для США и для всех случаев, когда страну определить не удалось.
 * Именно он лежит в начальном состоянии: если запрос не успел или упал,
 * пользователь видит этот вариант — так требует постановка.
 */
export const DEFAULT_PAYWALL_VARIANT = 'us'

/**
 * Все существующие варианты. Список нужен только для проверки значения из URL:
 * без него опечатка вроде ?paywallVariant=intll молча дала бы базовые тексты,
 * и было бы непонятно, то ли вариант так выглядит, то ли параметр не сработал.
 * Держать в синхроне с app.paywall.* на бэкенде.
 */
export const KNOWN_PAYWALL_VARIANTS = [DEFAULT_PAYWALL_VARIANT, 'intl']

/**
 * Имя GET-параметра, которым вариант форсируется вручную.
 */
export const PAYWALL_VARIANT_PARAM = 'paywallVariant'

/**
 * Вариант, принудительно заданный через ?paywallVariant=... — чтобы смотреть
 * любой вариант без VPN. Возвращает null, если параметра нет или значение
 * незнакомое.
 */
export const forcedPaywallVariant = (search) => {
  const raw = new URLSearchParams(
    typeof search === 'string' ? search : window.location.search
  ).get(PAYWALL_VARIANT_PARAM)

  if (raw === null) {
    return null
  }

  const variant = raw.trim().toLowerCase()

  if (KNOWN_PAYWALL_VARIANTS.indexOf(variant) === -1) {
    console.warn(
      `[paywall] неизвестный вариант "${raw}" в ?${PAYWALL_VARIANT_PARAM}, игнорирую. ` +
      `Доступны: ${KNOWN_PAYWALL_VARIANTS.join(', ')}`
    )

    return null
  }

  return variant
}

/**
 * Прокидывает форсированный вариант во все запросы к нашему API.
 *
 * Тексты внутри листа Apple/Google Pay собираются целиком на бэкенде, поэтому
 * подменить их на фронте нельзя — параметр обязан доехать до сервера. Вместо
 * правки каждого платёжного атома вешаем один интерцептор.
 *
 * Только наш API: сторонние запросы (ipify, rewardful) трогать незачем.
 */
export const installForcedVariantInterceptor = () => {
  const forced = forcedPaywallVariant()

  if (forced === null) {
    return
  }

  axios.interceptors.request.use(config => {
    const url = config.url || ''
    const isOwnApi = common.api === '' ? url.startsWith('/') : url.indexOf(common.api) === 0

    if (isOwnApi) {
      config.params = { ...(config.params || {}), [PAYWALL_VARIANT_PARAM]: forced }
    }

    return config
  })
}

/**
 * Что показывать после ответа сервера.
 *
 * Форс из URL перебивает геолокацию — ради этого параметр и нужен. Но если
 * окружение запретило перебивание (`overrideAllowed: false`), уважаем запрет:
 * иначе страница показала бы оффер, который сервер не подтвердит ни в платёжном
 * листе, ни при записи подписки.
 */
export const applyForcedVariant = (forced, serverData) => {
  const allowed = serverData.overrideAllowed !== false
  const applied = forced !== null && allowed ? forced : null

  return {
    variant: applied !== null ? applied : serverData.variant,
    country: serverData.country || null,
    forced: applied !== null,
    rejected: forced !== null && !allowed
  }
}

export const SetPaywallVariant = declareAction('setPaywallVariantAction')

/**
 * Вариант, зафиксированный за уже оформленной подпиской.
 * Приходит с бэкенда вместе с данными подписчика и используется в membership,
 * чтобы показать те же условия, что были при оплате.
 */
export const SetSubscriptionPaywallVariant = declareAction('setSubscriptionPaywallVariantAction')

export const GetPaywallVariant = declareAction(
  'getPaywallVariantAction',
  (payload, store) => {
    const forced = forcedPaywallVariant()

    if (forced !== null) {
      // Применяем сразу, не дожидаясь сети: иначе на медленном соединении
      // сначала мигнёт вариант по умолчанию.
      store.dispatch(SetPaywallVariant({ variant: forced, country: null, forced: true }))
      console.info(`[paywall] вариант принудительно задан через URL: ${forced}`)
    }

    axios.get(`${common.api}/paywall-context`)
      .then(response => {
        const { rejected, ...state } = applyForcedVariant(forced, response.data)

        if (rejected) {
          console.warn(
            `[paywall] ?${PAYWALL_VARIANT_PARAM} отключён на этом окружении, ` +
            'вариант определяется по IP'
          )
        }

        // Страну всегда показываем настоящую, чтобы в отладке было видно,
        // что именно определил сервер.
        store.dispatch(SetPaywallVariant(state))
      })
      .catch(() => {
        // Осознанно молчим: пейволл должен показаться в любом случае,
        // а «страну определить не удалось» и означает вариант по умолчанию.
      })
  }
)

const initState = {
  variant: DEFAULT_PAYWALL_VARIANT,
  country: null,
  // Пришёл ли ответ сервера. Нужно только для аналитики и отладки —
  // рендер на это не завязан, иначе пришлось бы блокировать показ пейволла.
  resolved: false,
  // null у подписок, оформленных до появления вариантов.
  subscriptionVariant: null,
  // Вариант задан вручную через ?paywallVariant=... Отключает событие показа
  // в аналитике, чтобы тестовые заходы не искажали статистику по вариантам.
  forced: false
}

export const PaywallVariantAtom = declareAtom(
  'paywallVariantAtom',
  initState,
  on => [
    on(SetPaywallVariant, (state, payload) => {
      return {
        ...state,
        variant: payload.variant || DEFAULT_PAYWALL_VARIANT,
        country: payload.country || null,
        resolved: true,
        forced: payload.forced === true
      }
    }),
    on(SetSubscriptionPaywallVariant, (state, payload) => {
      return { ...state, subscriptionVariant: payload || null }
    })
  ])
